import { useState } from "react";

import { isEmpty } from "lodash/fp";
import { useNavigate } from "react-router-dom";

import Table from "@app/analytics/components/Table";
import TableBody from "@app/analytics/components/Table/TableBody";
import TableHead from "@app/analytics/components/Table/TableHead";
import DetailsModal from "@app/analytics/MeetingReports/IncompleteMeetingReports/DetailsModal";
import { ReportSortUtils } from "@app/analytics/MeetingReports/ReportsTable/utils";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import type { UserMeeting } from "@app/common/types";

import IncompleteMeetingReportsEmptyState from "./IncompleteMeetingReportsEmptyState";
import {
  getIncompleteReason,
  IncompleteDetailsWithFix,
} from "./incompleteReasonDetails";
import IncompleteReportTableRow from "./IncompleteReportTableRow";
import { isUnseen } from "../utils";

interface IncompleteMeetingReportsTableProps {
  isLoading: boolean;
  lastSeenDate?: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
  };
  reports: UserMeeting[];
}

const HEAD_CELLS: HeadCell<UserMeeting>[] = [
  {
    id: "source",
    label: "Source",
    align: "center",
    sortBy: ReportSortUtils.source,
  },
  {
    id: "title",
    label: "Report",
    sortBy: ReportSortUtils.title,
  },
  {
    id: "date",
    label: "Date & Time",
    sortBy: ReportSortUtils.date,
  },
  {
    id: "role",
    label: "Your role",
    sortable: true,
    sortBy: (m) => m.ownerIsCurrentUser,
  },
  {
    id: "reason",
    label: "Reason",
    sortable: true,
    sortBy: getIncompleteReason,
  },
  {
    id: "details",
    label: "Details",
    sortable: false,
  },
];

const IncompleteMeetingReportsTable = ({
  reports,
  isLoading,
  pagination,
  lastSeenDate,
}: IncompleteMeetingReportsTableProps) => {
  const navigate = useNavigate();

  const [issueDetails, setIssueDetails] =
    useState<IncompleteDetailsWithFix | null>(null);

  const { handleSort, order, orderBy, sortedData } = useDataTable(
    reports,
    HEAD_CELLS,
    {
      defaultOrderBy: "date",
      defaultOrder: "desc",
    }
  );

  const meetingsForPage = sortedData.slice(
    pagination.pageSize * pagination.currentPage,
    pagination.pageSize * (pagination.currentPage + 1)
  );

  const handleOpenDetails = (details: IncompleteDetailsWithFix) => {
    setIssueDetails(details);
  };

  const handleCloseDetails = () => setIssueDetails(null);
  const handleClickCta = (details: IncompleteDetailsWithFix) => {
    navigate(details.fixCtaLink);
  };

  return (
    <>
      <Table sx={{ width: "100%" }}>
        <TableHead
          headCells={HEAD_CELLS}
          onSort={handleSort}
          order={order}
          orderBy={orderBy}
        />
        <TableBody loading={isLoading}>
          {isEmpty(sortedData) ? (
            <IncompleteMeetingReportsEmptyState />
          ) : (
            <>
              {meetingsForPage.map((report) => {
                const meetingIsUnseen = isUnseen(report, lastSeenDate);
                return (
                  <IncompleteReportTableRow
                    key={report.id}
                    report={report}
                    onOpenDetails={handleOpenDetails}
                    unseen={meetingIsUnseen}
                  />
                );
              })}
            </>
          )}
        </TableBody>
      </Table>

      <DetailsModal
        details={issueDetails}
        onClickCta={handleClickCta}
        open={Boolean(issueDetails)}
        onClose={handleCloseDetails}
      />
    </>
  );
};

export default IncompleteMeetingReportsTable;
